import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Copy, Eye, EyeOff, Key, Trash2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const AVAILABLE_SCOPES = [
  { value: 'deals:read', label: 'Read Deals' },
  { value: 'deals:write', label: 'Write Deals' },
  { value: 'athletes:read', label: 'Read Athletes' },
  { value: 'athletes:write', label: 'Write Athletes' },
  { value: '*', label: 'All Access' }
];

export default function ApiManagement() {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>('sandbox');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set());

  const queryClient = useQueryClient();

  const { data: apiKeys, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  const { data: usageStats } = useQuery({
    queryKey: ['api-usage-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('api_request_logs')
        .select('api_key_id, status_code, created_at')
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());
      
      if (error) throw error;
      return data;
    }
  });

  const createKeyMutation = useMutation({
    mutationFn: async () => {
      const keyValue = `ak_${environment}_${crypto.randomUUID().replace(/-/g, '').substring(0, 32)}`;
      
      const encoder = new TextEncoder();
      const data = encoder.encode(keyValue);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const keyHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      const { data: newKey, error } = await supabase
        .from('api_keys')
        .insert({
          key_hash: keyHash,
          key_prefix: keyValue.substring(0, 12),
          name: newKeyName,
          environment,
          scopes: selectedScopes,
          is_active: true
        } as any)
        .select()
        .single();

      if (error) throw error;
      return { key: newKey, value: keyValue };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setCreatedKey(data.value);
      setNewKeyName("");
      setSelectedScopes([]);
      toast.success("API key created successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to create API key: ${error.message}`);
    }
  });

  const deleteKeyMutation = useMutation({
    mutationFn: async (keyId: string) => {
      const { error } = await supabase
        .from('api_keys')
        .delete()
        .eq('id', keyId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success("API key revoked successfully");
    }
  });

  const toggleKeyReveal = (keyId: string) => {
    setRevealedKeys(prev => {
      const next = new Set(prev);
      if (next.has(keyId)) {
        next.delete(keyId);
      } else {
        next.add(keyId);
      }
      return next;
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const getUsageForKey = (keyId: string) => {
    if (!usageStats) return { total: 0, success: 0, errors: 0 };
    const keyStats = usageStats.filter(s => s.api_key_id === keyId);
    return {
      total: keyStats.length,
      success: keyStats.filter(s => s.status_code >= 200 && s.status_code < 300).length,
      errors: keyStats.filter(s => s.status_code >= 400).length
    };
  };

  return (
    <div className="container mx-auto py-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold">API Management</h1>
          <p className="text-muted-foreground mt-2">
            Manage API keys and monitor usage
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create API Key
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New API Key</DialogTitle>
              <DialogDescription>
                Generate a new API key for accessing the AthleteNexus API
              </DialogDescription>
            </DialogHeader>

            {createdKey ? (
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-lg">
                  <p className="text-sm font-medium mb-2">Your API Key (copy and save it now)</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-2 bg-background rounded text-sm font-mono">
                      {createdKey}
                    </code>
                    <Button size="icon" variant="outline" onClick={() => copyToClipboard(createdKey)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    ⚠️ This is the only time you'll see the full key. Store it securely.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="keyName">Key Name</Label>
                  <Input
                    id="keyName"
                    placeholder="e.g., Production API Key"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="environment">Environment</Label>
                  <Select value={environment} onValueChange={(val: any) => setEnvironment(val)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sandbox">Sandbox</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Scopes</Label>
                  <div className="space-y-2 mt-2">
                    {AVAILABLE_SCOPES.map(scope => (
                      <div key={scope.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={scope.value}
                          checked={selectedScopes.includes(scope.value)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedScopes([...selectedScopes, scope.value]);
                            } else {
                              setSelectedScopes(selectedScopes.filter(s => s !== scope.value));
                            }
                          }}
                        />
                        <label htmlFor={scope.value} className="text-sm cursor-pointer">
                          {scope.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              {createdKey ? (
                <Button onClick={() => {
                  setCreatedKey(null);
                  setShowCreateDialog(false);
                }}>
                  Done
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => createKeyMutation.mutate()}
                    disabled={!newKeyName || selectedScopes.length === 0}
                  >
                    Generate Key
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6">
        {isLoading ? (
          <Card>
            <CardContent className="py-8">
              <p className="text-center text-muted-foreground">Loading API keys...</p>
            </CardContent>
          </Card>
        ) : apiKeys && apiKeys.length > 0 ? (
          apiKeys.map((key) => {
            const usage = getUsageForKey(key.id);
            const isRevealed = revealedKeys.has(key.id);
            
            return (
              <Card key={key.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Key className="h-5 w-5" />
                        {key.name}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Created {new Date(key.created_at).toLocaleDateString()}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={key.environment === 'production' ? 'default' : 'secondary'}>
                        {key.environment}
                      </Badge>
                      <Badge variant={key.is_active ? 'default' : 'destructive'}>
                        {key.is_active ? 'Active' : 'Disabled'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">API Key</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 p-2 bg-muted rounded text-sm font-mono">
                        {isRevealed ? `${key.key_prefix}•••••••••••••••••••` : `${key.key_prefix}•••••••••••••••••••`}
                      </code>
                      <Button 
                        size="icon" 
                        variant="outline"
                        onClick={() => toggleKeyReveal(key.id)}
                      >
                        {isRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button 
                        size="icon" 
                        variant="outline"
                        onClick={() => copyToClipboard(key.key_prefix)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground">Scopes</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {key.scopes.map((scope: string) => (
                        <Badge key={scope} variant="outline">{scope}</Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground">Usage (Last 24h)</Label>
                    <div className="flex gap-4 mt-1">
                      <div>
                        <span className="text-2xl font-bold">{usage.total}</span>
                        <span className="text-sm text-muted-foreground ml-1">requests</span>
                      </div>
                      <div>
                        <span className="text-2xl font-bold text-green-600">{usage.success}</span>
                        <span className="text-sm text-muted-foreground ml-1">success</span>
                      </div>
                      <div>
                        <span className="text-2xl font-bold text-red-600">{usage.errors}</span>
                        <span className="text-sm text-muted-foreground ml-1">errors</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="text-sm text-muted-foreground">
                      Rate Limit: {key.rate_limit_per_minute}/min, {key.rate_limit_per_hour}/hr
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteKeyMutation.mutate(key.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Revoke
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Key className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No API Keys Yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first API key to start integrating with AthleteNexus
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create API Key
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
